import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Lock, Loader2, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import { supabase } from '@/lib/supabase/client';
import { useTranslation } from '@/lib/i18n';

/**
 * Three-layer recovery detection:
 *
 * Layer 1 (index.html): Inline script captures URL params into sessionStorage
 *   BEFORE ES modules load (Supabase singleton clears URL during init).
 *
 * Layer 2 (onAuthStateChange): Catches PASSWORD_RECOVERY if the PKCE
 *   code exchange completes AFTER the listener is registered.
 *
 * Layer 3 (getSession): Awaits Supabase initialization, then checks for
 *   an active session — handles the case where both layers 1 and 2 miss.
 */

function consumeRecoveryFlag(): boolean {
  try {
    if (sessionStorage.getItem('__turn_recovery')) {
      sessionStorage.removeItem('__turn_recovery');
      return true;
    }
  } catch { /* private browsing */ }
  return false;
}

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'request' | 'reset' | 'loading'>(
    consumeRecoveryFlag() ? 'loading' : 'request'
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setMode('reset');
        setError(null);
        setSuccess(null);
      }
    });

    // getSession() awaits Supabase's initialization promise — by the time
    // it resolves, any URL token exchange has already completed. This is
    // the bulletproof layer: works regardless of CSP, sessionStorage, or
    // onAuthStateChange timing.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setMode('reset');
      } else if (mode === 'loading') {
        setMode('request');
        setError(t('This reset link has expired or is invalid. Please request a new one.'));
      }
    });

    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (resetError) {
        setError(t('Something went wrong. Please try again.'));
      } else {
        setSuccess(t('Check your email for a password reset link. It may take a minute to arrive.'));
      }
    } catch {
      setError(t('An unexpected error occurred. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (password.length < 8) {
      setError(t('Password must be at least 8 characters.'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('Passwords do not match.'));
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });

      if (updateError) {
        const isSessionError = updateError.message.toLowerCase().includes('session')
          || updateError.message.toLowerCase().includes('not authenticated')
          || updateError.message.toLowerCase().includes('jwt');
        setError(isSessionError
          ? t('Your reset link has expired. Please request a new one.')
          : updateError.message);
      } else {
        setSuccess(t('Password updated successfully! Redirecting to home...'));
        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
      }
    } catch {
      setError(t('An unexpected error occurred. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  if (mode === 'loading') {
    return (
      <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center p-4">
        <Loader2 className="w-8 h-8 text-[#5C2D91] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img
              src="/turn-2.png"
              alt="Turn Literacy Logo"
              className="h-16 w-auto object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold text-[#1C1C1E] mb-2">
            {mode === 'request' ? t('Reset Your Password') : t('Set New Password')}
          </h1>
          <p className="text-[#636366]">
            {mode === 'request'
              ? t("Enter your email and we'll send you a reset link.")
              : t('Choose a new password for your account.')}
          </p>
        </div>

        <div
          className="rounded-3xl p-8"
          style={{
            background: 'rgba(255,255,255,0.05)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          {error && (
            <div className="flex items-center gap-2 p-4 bg-[#FF3B30]/10 border border-[#FF3B30]/20 rounded-xl text-[#FF3B30] mb-6">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2 p-4 bg-[#34C759]/10 border border-[#34C759]/20 rounded-xl text-[#34C759] mb-6">
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{success}</span>
            </div>
          )}

          {mode === 'request' ? (
            <form onSubmit={handleRequestReset} className="space-y-5">
              <div>
                <label htmlFor="reset-email" className="block text-sm font-medium text-[#1C1C1E] mb-2">
                  {t('Email Address')}
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8E8E93]" />
                  <input
                    id="reset-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="w-full pl-12 pr-4 py-3 bg-black/[0.03] border border-black/[0.08] rounded-xl text-[#1C1C1E] placeholder-[#8E8E93] focus:ring-2 focus:ring-[#5C2D91] focus:border-transparent transition-all"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-[#5C2D91] text-white rounded-xl font-semibold hover:bg-[#5C2D91]/80 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('Sending reset link...')}
                  </>
                ) : (
                  t('Send Reset Link')
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-5">
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-[#1C1C1E] mb-2">
                  {t('New Password')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8E8E93]" />
                  <input
                    id="new-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('Minimum 8 characters')}
                    required
                    minLength={8}
                    className="w-full pl-12 pr-4 py-3 bg-black/[0.03] border border-black/[0.08] rounded-xl text-[#1C1C1E] placeholder-[#8E8E93] focus:ring-2 focus:ring-[#5C2D91] focus:border-transparent transition-all"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-[#1C1C1E] mb-2">
                  {t('Confirm Password')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8E8E93]" />
                  <input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('Re-enter your password')}
                    required
                    minLength={8}
                    className="w-full pl-12 pr-4 py-3 bg-black/[0.03] border border-black/[0.08] rounded-xl text-[#1C1C1E] placeholder-[#8E8E93] focus:ring-2 focus:ring-[#5C2D91] focus:border-transparent transition-all"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-[#5C2D91] text-white rounded-xl font-semibold hover:bg-[#5C2D91]/80 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('Updating password...')}
                  </>
                ) : (
                  t('Update Password')
                )}
              </button>
            </form>
          )}
        </div>

        <div className="text-center mt-6">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm text-[#5C2D91] hover:text-[#5C2D91]/80 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('Back to Login')}
          </Link>
        </div>
      </div>
    </div>
  );
}
